/* eslint-disable import/no-extraneous-dependencies */
import * as amplitude from '@amplitude/analytics-browser';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { baseApi, client, firebaseAuthClient } from 'api/baseApi.ts';
import { signInService } from 'api/services/AppService';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import { RENDERNET_BASE_URL_DESKTOP_LOGIN } from 'core/constants/constants';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AuthenticationStatus } from 'core/constants/enums/AuthenticationStatus.ts';
import { AuthProcess } from 'core/constants/enums/AuthProcess.ts';
import { LogoutEnum } from 'core/constants/enums/LogoutEnum.ts';
import { PreloadChannels } from 'core/constants/enums/PreloadChannels.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import {
	GoogleAnalyticsAction,
	GoogleAnalyticsCategory,
} from 'core/constants/googleAnalyticsEvents';
import { gtagAnalyticsEvents } from 'core/constants/gtagAnalyticsEvents';
import { metaAnalyticsEvents } from 'core/constants/metaAnalyticsEvents';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { redditAnalyticsEvents } from 'core/constants/redditAnalyticsEvents';
import { twitterAnalyticsEvents } from 'core/constants/twitterAnalyticsEvents';
import { DESKTOP_LOGIN, HISTORY, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import {
	closeAllCanvasTabs,
	displayNameSelector,
	feedPromptModalDetailsSelector,
	feedPromptsModalSelectedBatchIdSelector,
	feedPromptsModalSelectedImageIndexSelector,
	feedPromptsModalTypeSelector,
	getUserProfileAsync,
	setAuthenticateUserAPIStage,
	setCurrentOAuthToken,
	setCurrentUser,
	setDisplayName,
	setFeedPromptsModalSelectedImageIndex,
	setPusherSubscriptionStatus,
	setUserAuthenticationStatus,
	toggleDeletedAccountModal,
	toggleShowMarketingBanner,
	updateUserNameAsync,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import {
	clearSelectedCharacterStudio,
	listCharacters,
	listCharactersSelector,
	setSelectedCharacterStudio,
} from 'core/store/reducers/characterStudioReducer/CharaterStudioSlice';
import { studioHistorySelector } from 'core/store/reducers/profileReducer/ProfileSlice';
import {
	resetStudioControlNetImage,
	resetStudioFacelockImage,
	setGetStudioFeedAPIStage,
	setStudioCharacterLabEnabled,
	setStudioControlNetImage,
	setStudioFacelockImage,
	setStudioFeed,
	setStudioSelectedFluxModels,
	setStudioSelectedModels,
	setStudioSelectedStyles,
	setStudioSettings,
	studioFeedBatchWiseMapSelector,
	uploadStudioControlNetImageLinkAsync,
	uploadStudioFaceLockImageLinkAsync,
} from 'core/store/reducers/studioReducer/StudioSlice';
import { resetSliceStatesData } from 'core/store/resetSliceStates';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { captureDatadogErrors, captureDatadogInfo } from 'core/utils/datadogUtil';
import { getFirebaseTokenWithRetries } from 'core/utils/firebaseUtil';
import { captureGoogleAnalyticsEvent } from 'core/utils/googleAnalyticsUtil.ts';
import { gtagAnalyticsUtil } from 'core/utils/gtagAnalyticsUtil';
import { clearIndexedDB } from 'core/utils/IndexedDBUtils.ts';
import { clearLocalStorage } from 'core/utils/LocalStorageUtil';
import { metaAnalyticsUtil } from 'core/utils/metaAnalyticsUtil';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import { redditAnalyticsUtil } from 'core/utils/redditAnalyticsUtil';
import { twitterAnalyticsUtil } from 'core/utils/twitterAnalyticsUtil';
import { handleSomethingWentWrongErrorWithToast } from 'core/utils/utils';
import posthog from 'posthog-js';

const { GoogleAuthProvider } = firebaseAuthClient;
const auth = client.getFirebaseAuth();
const googleProvider = client.getGoogleAuthProvider();

export const googleSignOut = (reason) => (dispatch) => {
	try {
		client.auth
			.signOut(auth)
			.then(async () => {
				// Sending event to PostHog for sign-out.
				captureDatadogInfo('User logged out', { cause: reason });
				capturePosthogEvent(PostHogEvents.LOGGED_OUT, { cause: reason });
				captureAmplitudeEvent(AmplitudeEvents.LOGGED_OUT, { cause: reason });

				// Sign-out successful.
				dispatch(setUserAuthenticationStatus(AuthenticationStatus.UNAUTHENTICATED));
				dispatch(setCurrentUser(null));
				dispatch(closeAllCanvasTabs());
				dispatch(toggleShowMarketingBanner(false));

				// RESET FEED
				dispatch(setStudioFeed({}));
				dispatch(setGetStudioFeedAPIStage(ApiStage.IDLE));

				// RESET PUSHER SUBSCRIPTION STATUS
				dispatch(setPusherSubscriptionStatus(ApiStage.IDLE));

				//  NOTE posthog.identify
				posthog.reset();
				amplitude.reset();
				dispatch(resetSliceStatesData());
				clearLocalStorage();
				await clearIndexedDB('appData');
				dispatch(baseApi.util.resetApiState());
			})
			.catch((error) => {
				captureDatadogErrors('Sign out catch', {}, error);
			});
	} catch (err) {
		captureDatadogErrors('Sign out try catch', {}, err);
	}
};

export const setCurrentUserThunk = (user) => (dispatch) => {
	dispatch(
		setCurrentUser({
			id: user.id,
			uid: user.uid,
			email: user.email,
			displayName: user.displayName,
			photoURL: user.photoURL,
			username: user.username,
		}),
	);
};
export const verifyAuthToken = (idToken, user, authProcess) => (dispatch, getState) => {
	const name = displayNameSelector(getState());
	const userProfile = userProfileSelector(getState());
	signInService({ authToken: idToken })
		.then(() => {
			if (name !== null) {
				dispatch(updateUserNameAsync({ displayName: name }));
				dispatch(setDisplayName(null));
			}
			dispatch(getUserProfileAsync());
			dispatch(setUserAuthenticationStatus(AuthenticationStatus.AUTHENTICATED));
			dispatch(setCurrentUserThunk(user));
			dispatch(setAuthenticateUserAPIStage(ApiStage.SUCCESS));
			posthog.identify(user.email, {
				userPropertiesToSetOnce: {
					createdAt: userProfile?.createdAt,
				},
			});
			datadogRum.setUser({
				email: user.email,
			}); // Set Datadog RUM User ID
			datadogLogs.setUser({
				email: user.email,
			}); // Set Datadog Logs User ID
			if (authProcess === AuthProcess.SIGN_IN) {
				amplitude.setUserId(user.email); // Set Amplitude User ID
				const identify = new amplitude.Identify();
				identify.setOnce('createdAt', userProfile?.createdAt);
				amplitude.identify(identify);
				captureGoogleAnalyticsEvent(
					GoogleAnalyticsCategory.ACTION_STATUS,
					GoogleAnalyticsAction.SIGN_IN_SUCCESS,
				);
				gtagAnalyticsUtil(gtagAnalyticsEvents.SIGNUP_REQUEST);
				twitterAnalyticsUtil(twitterAnalyticsEvents.SIGNUP_REQUEST);
				redditAnalyticsUtil(redditAnalyticsEvents.SIGNUP_REQUEST);
				metaAnalyticsUtil(metaAnalyticsEvents.SIGNUP_REQUEST);
			}
		})
		.catch((err) => {
			if (authProcess === AuthProcess.SIGN_IN) {
				dispatch(googleSignOut(LogoutEnum.UNVERIFIED_TOKEN));
				dispatch(setUserAuthenticationStatus(AuthenticationStatus.UNAUTHENTICATED));
				if (err.code === RendernetErrors.DELETED_USER) {
					dispatch(toggleDeletedAccountModal(true));
					return;
				}
				handleSomethingWentWrongErrorWithToast('Auth token verification failed', err);
			}
		});
};
export const fetchAndVerifyAuthToken =
	(user, authProcess = AuthProcess.SIGN_IN) =>
	(dispatch) => {
		getFirebaseTokenWithRetries(3)
			.then((idToken) => {
				dispatch(setUserAuthenticationStatus(AuthenticationStatus.LOADING));
				dispatch(verifyAuthToken(idToken, user, authProcess));
			})
			.catch((error) => {
				// NOTE: Sign-out from firebase
				dispatch(googleSignOut(LogoutEnum.FIREBASE_TOKEN_CATCH));
				dispatch(setUserAuthenticationStatus(AuthenticationStatus.UNAUTHENTICATED));
				handleSomethingWentWrongErrorWithToast('fetch/verify auth token failed', error);
			});
	};

export const signInWithGoogleCredential = (token) => async (dispatch) => {
	try {
		const credential = GoogleAuthProvider.credential(token);
		const result = await client.auth.signInWithCredential(auth, credential);
		const { user } = result;
		dispatch(fetchAndVerifyAuthToken(user));
	} catch (error) {
		dispatch(setCurrentUser(null));
		handleSomethingWentWrongErrorWithToast('signInWithPopup catch', error);
	} finally {
		dispatch(setCurrentOAuthToken(null));
	}
};

export const signInWithGoogleElectronPopup = () => async (dispatch) => {
	try {
		await client.auth
			.signInWithPopup(auth, googleProvider)
			.then((result) => {
				const { _tokenResponse } = result;
				const oAuthToken = _tokenResponse?.oauthIdToken;
				dispatch(setCurrentOAuthToken(oAuthToken));
				if (window != null) {
					window.location.href = `rendernet://${oAuthToken}`;
				}
			})
			.catch(() => {
				dispatch(setCurrentUser(null));
			});
	} catch (err) {
		dispatch(setCurrentUser(null));
		handleSomethingWentWrongErrorWithToast('google sign in(electron) failed', err);
	}
};

export const signInWithGoogleElectronRedirect = () => async (dispatch) => {
	try {
		await client.auth.signInWithRedirect(auth, googleProvider);
	} catch (err) {
		dispatch(setCurrentUser(null));
		handleSomethingWentWrongErrorWithToast('google sign in(electron) failed', err);
	}
};

export const getGoogleLoginRedirectResult = () => async (dispatch) =>
	client.auth
		.getRedirectResult(auth)
		.then((result) => {
			if (result == null) {
				dispatch(signInWithGoogleElectronRedirect());
				return;
			}
			const { _tokenResponse } = result;
			const oAuthToken = _tokenResponse?.oauthIdToken;
			dispatch(setCurrentOAuthToken(oAuthToken));
			if (window != null) {
				window.location.href = `rendernet://${oAuthToken}`;
			}
		})
		.catch(() => {
			dispatch(setCurrentUser(null));
		});

export const signInWithGoogle = () => async (dispatch) => {
	try {
		if (window.electron) {
			window.electron.ipcRenderer.send(
				PreloadChannels.OPEN_EXTERNAL_LINK,
				`${RENDERNET_BASE_URL_DESKTOP_LOGIN}/${DESKTOP_LOGIN}`,
			);
		} else {
			client.auth
				.signInWithPopup(auth, googleProvider)
				.then((result) => {
					const { user } = result;
					dispatch(fetchAndVerifyAuthToken(user));
				})
				.catch((error) => {
					dispatch(setCurrentUser(null));
					handleSomethingWentWrongErrorWithToast('signInWithPopup catch', error);
				});
		}
	} catch (err) {
		dispatch(setCurrentUser(null));
		handleSomethingWentWrongErrorWithToast('signInWithPopup catch', err);
	}
};

export const handleReusePrompt = () => (dispatch, getState) => {
	const selectedPrompt = feedPromptModalDetailsSelector(getState());

	if (selectedPrompt?.styleId) {
		dispatch(setStudioSelectedStyles({ styles: [selectedPrompt.styleId], replace: true }));
		// dispatch(setStudioSidebarView(StudioSidebarViews.MAGICMODE));
		dispatch(setStudioSelectedFluxModels([]));
		dispatch(setStudioSelectedModels({ model: [], replace: true }));
	} else {
		dispatch(setStudioSelectedModels({ model: selectedPrompt?.model, replace: true }));
		// dispatch(setStudioSidebarView(StudioSidebarViews.PROMODE));
		dispatch(setStudioSelectedFluxModels([]));
		dispatch(setStudioSelectedStyles({ styles: [], replace: true }));
	}

	if (selectedPrompt.attributes) {
		const { controlNet, facelock, character } = selectedPrompt.attributes;
		const linkPrefix = 'https:/';
		const base64Prefix = 'data:image/jpeg;base64,';

		if (controlNet && controlNet?.inputImage) {
			if (controlNet?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadStudioControlNetImageLinkAsync(controlNet?.inputImage));
			} else {
				dispatch(setStudioControlNetImage(`${base64Prefix}${controlNet?.inputImage}`));
			}
		} else {
			dispatch(resetStudioControlNetImage());
		}
		if (facelock && facelock?.inputImage) {
			if (facelock?.inputImage.substring(0, linkPrefix.length) === linkPrefix) {
				dispatch(uploadStudioFaceLockImageLinkAsync(facelock?.inputImage));
			} else {
				dispatch(setStudioFacelockImage(`${base64Prefix}${facelock?.inputImage}`));
			}
		} else {
			dispatch(resetStudioFacelockImage());
		}
		if (character) {
			let allCharacters = listCharactersSelector(getState());
			let charactersPromise = Promise.resolve(true);
			if (!allCharacters.length) {
				charactersPromise = dispatch(listCharacters());
			}
			charactersPromise.then(() => {
				allCharacters = listCharactersSelector(getState());
				const selectedCharacter = allCharacters.find((value) => character.id === value?.id);
				if (selectedCharacter) {
					dispatch(setSelectedCharacterStudio(selectedCharacter));
					dispatch(setStudioCharacterLabEnabled(true));
				} else {
					dispatch(clearSelectedCharacterStudio());
				}
			});
		} else {
			dispatch(clearSelectedCharacterStudio());
		}
	}
	dispatch(
		setStudioSettings({
			settings: selectedPrompt,
			fromFeed: true,
			setControlNetImage: false,
			setFacelockImage: false,
		}),
	);
};

export const switchFeedPromptsModalSelectedImage =
	(step = 1) =>
	(dispatch, getState) => {
		const feedPromptsModalType = feedPromptsModalTypeSelector(getState());
		const feedPromptsModalSelectedImageIndex =
			feedPromptsModalSelectedImageIndexSelector(getState());

		const studioFeedBatchWiseMap = studioFeedBatchWiseMapSelector(getState());
		const feedPromptsModalSelectedBatchId = feedPromptsModalSelectedBatchIdSelector(getState());
		const batch = studioFeedBatchWiseMap[feedPromptsModalSelectedBatchId];

		const studioHistory = studioHistorySelector(getState());

		let imagesLength = 0;
		if (feedPromptsModalType === TEXT_TO_IMAGE) {
			imagesLength = Object.keys(batch.derivedMedia).length;
		}
		if (feedPromptsModalType === HISTORY) {
			imagesLength = studioHistory.length;
		}

		const min = 0;
		const max = imagesLength - 1;

		let newIndex = feedPromptsModalSelectedImageIndex + step;
		if (newIndex < min) newIndex = max;
		if (newIndex > max) newIndex = min;
		dispatch(setFeedPromptsModalSelectedImageIndex(newIndex));
	};

export const signInAnonymouslyFirebase = () => async (dispatch) => {
	try {
		await client.auth
			.signInAnonymously(auth)
			.then((result) => {
				const { user } = result;
				dispatch(setCurrentUserThunk(user));
			})
			.catch(() => {
				dispatch(setCurrentUser(null));
			});
	} catch (err) {
		dispatch(setCurrentUser(null));
		handleSomethingWentWrongErrorWithToast('Anonymous sign in failed', err);
	}
};
